
import { settingsService } from "@services/settings.service";
import { algoliaService } from "@services/algolia.service";
import { SearchClient } from "algoliasearch";
import { computed, defineComponent, Ref, ref, onMounted, getCurrentInstance } from "vue";
import { AlgoliaSearchHelper, SearchParameters } from "algoliasearch-helper";
import { debounce } from "throttle-debounce";
import { history } from "instantsearch.js/es/lib/routers";
import { singleIndex } from "instantsearch.js/es/lib/stateMappings";
import { Constants } from "@app/constants";
import CiPortfolioComparisonTile from "@components/portfolios/comparisons/comparison-tile.vue";
import { apiService } from "@services/api.service";
import { useLoader } from "@composables/loader";
import { ADS_INVENTORY } from "@models/constants/ads_inventory";
import CiPortfoliosComparisonFilter from "@components/portfolios/comparisons/comparison-filter.vue";
import { AisInstantSearch } from "vue-instantsearch";
import CiClearRefinements from "@components/base/ais/clear-refinements.vue";
import { Period } from "@models/enums";
import { ciTrackingService } from "@services/ci-tracking.service";
import { ViewportStoreModule } from "@stores/viewport.store";
import { getModule } from "vuex-module-decorators";
import DropdownMenu from "@components/base/dropdown-menu.vue";
import DropdownItem from "@components/base/dropdown-item.vue";
import { useI18n } from "@composables/i18n";

const ViewportStore = getModule(ViewportStoreModule);

type SortingOptions = {
  attribute: string;
  order: string;
};

export default defineComponent({
  name: "PortfoliosComparisonPage",
  components: {
    "ci-portfolio-comparison-tile": CiPortfolioComparisonTile,
    "ci-portfolios-comparison-filters": CiPortfoliosComparisonFilter,
    "ci-dropdown-menu": DropdownMenu,
    "ci-dropdown-menu-item": DropdownItem,

    CiClearRefinements,
  },
  setup() {
    const loader = useLoader();
    const { t } = useI18n(getCurrentInstance());
    const handleSortingChanged = (option: any) => {
      sorting.value = {
        attribute: option.attribute,
        order: option.sorting,
      };
    };

    const selectedIndex = (interval: Portfolios.PerformanceInterval) =>
      interval.availableIndexes[sorting.value.attribute];

    const intervalClass = (interval: Portfolios.PerformanceInterval) => {
      if (!selectedIndex(interval)) {
        return "disabled";
      } else if (selectedPerformanceInterval.value.performance_key === interval.performance_key) {
        return "active";
      }

      return "";
    };

    const sorting: Ref<SortingOptions> = ref({
      attribute: "perf",
      order: "desc",
    });

    const performanceIntervals: Portfolios.PerformanceInterval[] = [
      {
        period: 3,
        category: "month",
        performance_key: Period.MONTH_3,
        availableIndexes: { perf: "_perf_3m_" },
        label: "ui.shared.global.metrics.periods.month.abbr",
      },
      {
        period: 6,
        category: "month",
        performance_key: Period.MONTH_6,
        availableIndexes: { perf: "_perf_6m_" },
        label: "ui.shared.global.metrics.periods.month.abbr",
      },
      {
        category: "ytd",
        performance_key: Period.YEAR_YTD,
        availableIndexes: { perf: "_perf_ytd_" },
        label: "ui.shared.global.metrics.periods.ytd",
      },
      {
        period: 1,
        category: "year",
        performance_key: Period.YEAR_1,
        availableIndexes: { perf: "_perf_1y_", vola: "_vola_1y_" },
        label: "ui.shared.global.metrics.periods.year.abbr",
      },
      {
        period: 3,
        category: "year",
        performance_key: Period.YEAR_3,
        availableIndexes: { perf: "_perf_3y_", vola: "_vola_3y_" },
        label: "ui.shared.global.metrics.periods.year.abbr",
      },
      {
        period: 5,
        category: "year",
        performance_key: Period.YEAR_5,
        availableIndexes: { perf: "_perf_5y_", vola: "_vola_5y_" },
        label: "ui.shared.global.metrics.periods.year.abbr",
      },
      {
        period: 10,
        category: "year",
        performance_key: Period.YEAR_10,
        availableIndexes: { perf: "_perf_10y_", vola: "_vola_10y_" },
        label: "ui.shared.global.metrics.periods.year.abbr",
      },
    ];

    const selectedPerformanceInterval: Ref<Portfolios.PerformanceInterval> = ref({
      period: 1,
      category: "year",
      performance_key: Period.YEAR_1,
      availableIndexes: { perf: "_perf_1y_" },
      label: "ui.shared.global.metrics.periods.year.abbr",
    });

    const searchConfig: Indexable = {
      hitsPerPage: 15,
      filters: "_tags:in_portfolio_search",
    };
    const mobileMode = computed(() => ViewportStore.smartphone);

    const searchClient: SearchClient = algoliaService.getClient();
    const rawIndexName: string = settingsService.options.algolia_index_names.portfolios;
    const sortingPerPeriodIndexes: string[] = ["perf", "vola", "sharpe_ratio", "sri"];
    const indexName = computed((): string => {
      if (sortingPerPeriodIndexes.includes(sorting.value.attribute)) {
        if (!selectedIndex(selectedPerformanceInterval.value)) return "";

        return `${rawIndexName}${selectedIndex(selectedPerformanceInterval.value)}${sorting.value.order}`;
      }

      return `${rawIndexName}_${sorting.value.attribute}_${sorting.value.order}`;
    });

    const instantSearch: Ref<typeof AisInstantSearch | null> = ref(null);
    let lastSearchState: Nullable<SearchParameters> = null;
    const searchFunction = (helper: AlgoliaSearchHelper) => debouncedSearch(helper); // Debounce search for half a second
    const search = async (helper: AlgoliaSearchHelper) => {
      loader.start();
      try {
        helper.search();
      } catch (error) {
        apiService.handleAPIError(error);
      } finally {
        loader.stop();
      }
    };
    const routing: Indexable = {
      router: history(),
      stateMapping: singleIndex(indexName.value),
    };
    const debouncedSearch = debounce(Constants.defaultDelay, search);

    const handlePerformanceIntervalSelected = (interval: Portfolios.PerformanceInterval) => {
      if (selectedIndex(interval)) {
        selectedPerformanceInterval.value = interval;
      }
    };
    const onPaginationChange = async () => {
      setTimeout(() => {
        document.documentElement.scrollTop = 0;
      }, 200);
    };
    const adIds = computed(
      (): Record<string, string> => ({
        header: ADS_INVENTORY.portfolio_comparisons_header_slot.dom_id,
        skyscraper: ADS_INVENTORY.portfolio_comparisons_sidebar_top.dom_id,
        sidebar_2nd: ADS_INVENTORY.portfolio_comparisons_sidebar_2nd.dom_id,
      })
    );

    const filters: Nullable<any> = ref(null);
    const handleFiltersMenu = () => {
      if (!filters.value) return;
      if (instantSearch.value) lastSearchState = instantSearch.value.instantSearchInstance.helper.state;

      filters.value.toggleMenu();
    };

    const handleResetFilters = () => {
      if (instantSearch.value) {
        const { helper } = instantSearch.value.instantSearchInstance;
        helper.state = lastSearchState;
        debouncedSearch(helper);
      }
    };

    const transformCurrentRefinements = (items: any[]) =>
      items.map((item) => ({ ...item, label: t(`ui.views.portfolios.comparisons.${item.label}`) }));

    onMounted(() => {
      ciTrackingService.trackView("PortfolioSearch");
    });

    return {
      mobileMode,
      searchConfig,
      searchClient,
      selectedPerformanceInterval,
      indexName,
      routing,
      searchFunction,
      handlePerformanceIntervalSelected,
      performanceIntervals,
      instantSearch,
      onPaginationChange,
      adIds,
      handleSortingChanged,
      filters,
      handleFiltersMenu,
      handleResetFilters,
      transformCurrentRefinements,
      sorting,
      intervalClass,
    };
  },
});
